import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { config } from '../../config';
import './CampForm.css';

interface CampFormData {
  name: string;
  camp_type: string;
  tier: string; // Legacy field for backward compatibility
  applicable_tiers: string[]; // New: multiple tiers
  tier_pricing_model: string; // 'same_price' or 'different_price'
  tier_pricing: { [key: string]: { base_price?: string; weekly_price?: string; drop_in_price?: string } }; // Tier-specific pricing
  theme: string;
  description: string;
  note: string;
  start_date: string;
  end_date: string;
  camp_start_time: string;
  camp_end_time: string;
  timezone: string;
  snack_time_start: string;
  snack_time_end: string;
  lunch_break_start: string;
  lunch_break_end: string;
  early_dropoff_enabled: boolean;
  early_dropoff_start: string;
  early_dropoff_end: string;
  early_dropoff_fee: string;
  late_pickup_enabled: boolean;
  late_pickup_start: string;
  late_pickup_end: string;
  late_pickup_fee: string;
  pricing_model: string;
  base_price: string;
  weekly_price: string;
  drop_in_price: string;
  total_weeks: string;
  capacity: string;
  min_enrollment: string;
  age_min: string;
  age_max: string;
  prerequisites: string;
  materials_provided: string;
  materials_students_bring: string;
  lunch_provided: boolean;
  snacks_provided: boolean;
  dietary_restrictions_notes: string;
  location_name: string;
  location_address: string;
  room_number: string;
  staff_ratio: string;
  cancellation_policy: string;
  refund_policy: string;
  stripe_mode: string;
  discount_codes_enabled: boolean;
}

interface CampFormProps {
  camp: any | null;
  onClose: () => void;
  onSave: () => void;
}

interface CampDay {
  date: string;
  dayNumber: number;
  weekNumber: number;
  isAvailable: boolean;
  unavailableReason: string;
  dayOfWeek: string;
}

const CampForm: React.FC<CampFormProps> = ({ camp, onClose, onSave }) => {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [campDays, setCampDays] = useState<CampDay[]>([]);
  const [formData, setFormData] = useState<CampFormData>({
    name: '',
    camp_type: 'winter_camp',
    tier: 'all', // Legacy
    applicable_tiers: ['foundations', 'innovators', 'pioneers'],
    tier_pricing_model: 'same_price',
    tier_pricing: {},
    theme: '',
    description: '',
    note: '',
    start_date: '',
    end_date: '',
    camp_start_time: '09:00',
    camp_end_time: '16:00',
    timezone: 'America/Toronto',
    snack_time_start: '10:30',
    snack_time_end: '10:45',
    lunch_break_start: '12:00',
    lunch_break_end: '13:00',
    early_dropoff_enabled: false,
    early_dropoff_start: '08:00',
    early_dropoff_end: '09:00',
    early_dropoff_fee: '10.00',
    late_pickup_enabled: false,
    late_pickup_start: '15:00',
    late_pickup_end: '16:00',
    late_pickup_fee: '10.00',
    pricing_model: 'weekly_and_drop_in',
    base_price: '',
    weekly_price: '200.00',
    drop_in_price: '60.00',
    total_weeks: '2',
    capacity: '20',
    min_enrollment: '5',
    age_min: '6',
    age_max: '12',
    prerequisites: '',
    materials_provided: '',
    materials_students_bring: '',
    lunch_provided: false,
    snacks_provided: true,
    dietary_restrictions_notes: '',
    location_name: '',
    location_address: '',
    room_number: '',
    staff_ratio: '1:8',
    cancellation_policy: '',
    refund_policy: '',
    stripe_mode: 'test',
    discount_codes_enabled: false
  });

  useEffect(() => {
    if (camp) {
      // Populate form with existing camp data
      setFormData({
        name: camp.name || '',
        camp_type: camp.camp_type || 'winter_camp',
        tier: camp.tier || 'all', // Legacy
        applicable_tiers: camp.applicable_tiers || (camp.tier === 'all' ? ['foundations', 'innovators', 'pioneers'] : [camp.tier]),
        tier_pricing_model: camp.tier_pricing_model || 'same_price',
        tier_pricing: camp.tier_pricing || {},
        theme: camp.theme || '',
        description: camp.description || '',
        note: camp.note || '',
        start_date: camp.start_date || '',
        end_date: camp.end_date || '',
        camp_start_time: camp.camp_start_time || '09:00',
        camp_end_time: camp.camp_end_time || '16:00',
        timezone: camp.timezone || 'America/Toronto',
        snack_time_start: camp.snack_time_start || '10:30',
        snack_time_end: camp.snack_time_end || '10:45',
        lunch_break_start: camp.lunch_break_start || '12:00',
        lunch_break_end: camp.lunch_break_end || '13:00',
        early_dropoff_enabled: camp.early_dropoff_enabled || false,
        early_dropoff_start: camp.early_dropoff_start || '08:00',
        early_dropoff_end: camp.early_dropoff_end || '09:00',
        early_dropoff_fee: camp.early_dropoff_fee?.toString() || '10.00',
        late_pickup_enabled: camp.late_pickup_enabled || false,
        late_pickup_start: camp.late_pickup_start || '15:00',
        late_pickup_end: camp.late_pickup_end || '16:00',
        late_pickup_fee: camp.late_pickup_fee?.toString() || '10.00',
        pricing_model: camp.pricing_model || 'full_camp',
        base_price: camp.base_price?.toString() || '',
        weekly_price: camp.weekly_price?.toString() || '',
        drop_in_price: camp.drop_in_price?.toString() || '',
        total_weeks: camp.total_weeks?.toString() || '1',
        capacity: camp.capacity?.toString() || '20',
        min_enrollment: camp.min_enrollment?.toString() || '5',
        age_min: camp.age_min?.toString() || '6',
        age_max: camp.age_max?.toString() || '12',
        prerequisites: camp.prerequisites || '',
        materials_provided: camp.materials_provided?.join(', ') || '',
        materials_students_bring: camp.materials_students_bring?.join(', ') || '',
        lunch_provided: camp.lunch_provided || false,
        snacks_provided: camp.snacks_provided !== false,
        dietary_restrictions_notes: camp.dietary_restrictions_notes || '',
        location_name: camp.location_name || '',
        location_address: camp.location_address || '',
        room_number: camp.room_number || '',
        staff_ratio: camp.staff_ratio || '1:8',
        cancellation_policy: camp.cancellation_policy || '',
        refund_policy: camp.refund_policy || '',
        stripe_mode: camp.stripe_mode || 'test',
        discount_codes_enabled: camp.discount_codes_enabled || false
      });
    }
  }, [camp]);

  // Generate camp days when start/end dates change
  useEffect(() => {
    if (formData.start_date && formData.end_date) {
      generateCampDays();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.start_date, formData.end_date, formData.total_weeks]);

  const generateCampDays = () => {
    if (!formData.start_date || !formData.end_date) return;
    
    // Parse dates as local dates to avoid timezone shifts
    const [startYear, startMonth, startDay] = formData.start_date.split('-').map(Number);
    const [endYear, endMonth, endDay] = formData.end_date.split('-').map(Number);
    
    const start = new Date(startYear, startMonth - 1, startDay);
    const end = new Date(endYear, endMonth - 1, endDay);
    const days: CampDay[] = [];
    
    if (start > end) return;
    
    const totalDays = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
    const totalWeeks = parseInt(formData.total_weeks) || 1;
    const daysPerWeek = Math.ceil(totalDays / totalWeeks);
    
    let dayNumber = 1;
    const current = new Date(start);
    
    while (current <= end) {
      const weekNumber = Math.ceil(dayNumber / daysPerWeek);
      const dayOfWeek = current.toLocaleDateString('en-US', { weekday: 'long' });
      const year = current.getFullYear();
      const month = String(current.getMonth() + 1).padStart(2, '0');
      const day = String(current.getDate()).padStart(2, '0');
      const dateStr = `${year}-${month}-${day}`;
      
      days.push({
        date: dateStr,
        dayNumber: dayNumber,
        weekNumber: Math.min(weekNumber, totalWeeks),
        isAvailable: true,
        unavailableReason: '',
        dayOfWeek: dayOfWeek
      });
      
      dayNumber++;
      current.setDate(current.getDate() + 1);
    }
    
    setCampDays(days);
  };

  const updateDay = (index: number, field: keyof CampDay, value: any) => {
    const updated = [...campDays];
    updated[index] = { ...updated[index], [field]: value };
    setCampDays(updated);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async () => {
    setLoading(true);
    const token = localStorage.getItem('cognitoToken') || '';
    
    try {
      // Prepare data for API
      const apiData: any = {
        name: formData.name,
        camp_type: formData.camp_type,
        tier: formData.tier,
        theme: formData.theme || null,
        description: formData.description || null,
        note: formData.note || null,
        start_date: formData.start_date,
        end_date: formData.end_date,
        camp_start_time: formData.camp_start_time || null,
        camp_end_time: formData.camp_end_time || null,
        timezone: formData.timezone,
        snack_time_start: formData.snack_time_start || null,
        snack_time_end: formData.snack_time_end || null,
        lunch_break_start: formData.lunch_break_start || null,
        lunch_break_end: formData.lunch_break_end || null,
        early_dropoff_enabled: formData.early_dropoff_enabled,
        early_dropoff_start: formData.early_dropoff_enabled ? formData.early_dropoff_start : null,
        early_dropoff_end: formData.early_dropoff_enabled ? formData.early_dropoff_end : null,
        early_dropoff_fee: formData.early_dropoff_enabled ? parseFloat(formData.early_dropoff_fee) : null,
        late_pickup_enabled: formData.late_pickup_enabled,
        late_pickup_start: formData.late_pickup_enabled ? formData.late_pickup_start : null,
        late_pickup_end: formData.late_pickup_enabled ? formData.late_pickup_end : null,
        late_pickup_fee: formData.late_pickup_enabled ? parseFloat(formData.late_pickup_fee) : null,
        pricing_model: formData.pricing_model,
        base_price: formData.base_price ? parseFloat(formData.base_price) : null,
        weekly_price: formData.weekly_price ? parseFloat(formData.weekly_price) : null,
        drop_in_price: formData.drop_in_price ? parseFloat(formData.drop_in_price) : null,
        applicable_tiers: formData.applicable_tiers,
        tier_pricing_model: formData.tier_pricing_model,
        tier_pricing: formData.tier_pricing_model === 'different_price' ? 
          Object.fromEntries(
            Object.entries(formData.tier_pricing).map(([tier, prices]) => [
              tier,
              {
                base_price: prices.base_price ? parseFloat(prices.base_price) : null,
                weekly_price: prices.weekly_price ? parseFloat(prices.weekly_price) : null,
                drop_in_price: prices.drop_in_price ? parseFloat(prices.drop_in_price) : null
              }
            ])
          ) : null,
        total_weeks: parseInt(formData.total_weeks),
        capacity: parseInt(formData.capacity),
        min_enrollment: parseInt(formData.min_enrollment),
        age_min: formData.age_min ? parseInt(formData.age_min) : null,
        age_max: formData.age_max ? parseInt(formData.age_max) : null,
        prerequisites: formData.prerequisites || null,
        materials_provided: formData.materials_provided 
          ? formData.materials_provided.split(',').map(m => m.trim()).filter(m => m) 
          : null,
        materials_students_bring: formData.materials_students_bring 
          ? formData.materials_students_bring.split(',').map(m => m.trim()).filter(m => m) 
          : null,
        lunch_provided: formData.lunch_provided,
        snacks_provided: formData.snacks_provided,
        dietary_restrictions_notes: formData.dietary_restrictions_notes || null,
        location_name: formData.location_name || null,
        location_address: formData.location_address || null,
        room_number: formData.room_number || null,
        staff_ratio: formData.staff_ratio || null,
        cancellation_policy: formData.cancellation_policy || null,
        refund_policy: formData.refund_policy || null,
        camp_days: campDays.map(day => ({
          day_number: day.dayNumber,
          week_number: day.weekNumber,
          date: day.date,
          is_available: day.isAvailable,
          unavailable_reason: day.unavailableReason || null
        }))
      };

      const url = camp 
        ? `${config.BACKEND_URL}/api/camps/${camp.id}`
        : `${config.BACKEND_URL}/api/camps`;
      
      const method = camp ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(apiData)
      });

      if (response.ok) {
        const result = await response.json();
        alert(`Camp ${camp ? 'updated' : 'created'} successfully!${!camp ? ` with ${campDays.filter(d => d.isAvailable).length} available days configured.` : ''}`);
        
        onSave();
      } else {
        const error = await response.json();
        alert(`Error: ${error.detail || 'Failed to save camp'}`);
      }
    } catch (error) {
      console.error('Error saving camp:', error);
      alert('Error saving camp');
    } finally {
      setLoading(false);
    }
  };

  const generateCampDaysFromAPI = async (campId: string) => {
    try {
      const token = localStorage.getItem('cognitoToken') || '';
      const response = await fetch(`${config.BACKEND_URL}/api/camps/${campId}/days/generate`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (response.ok) {
        alert('Camp days generated successfully!');
      }
    } catch (error) {
      console.error('Error generating days:', error);
    }
  };

  const nextStep = () => {
    if (currentStep < 6) setCurrentStep(currentStep + 1);
  };

  const prevStep = () => {
    if (currentStep > 1) setCurrentStep(currentStep - 1);
  };

  const steps = [
    { number: 1, title: 'Basic Info', icon: '📝' },
    { number: 2, title: 'Schedule', icon: '⏰' },
    { number: 3, title: 'Pricing', icon: '💰' },
    { number: 4, title: 'Details', icon: '📋' },
    { number: 5, title: 'Policies', icon: '📄' },
    { number: 6, title: 'Day Config', icon: '📅' }
  ];

  return (
    <div className="camp-form-overlay">
      <div className="camp-form-container">
        <div className="camp-form-header">
          <h2>{camp ? '✏️ Edit Camp' : '➕ Create New Camp'}</h2>
          <button className="close-btn" onClick={onClose}>✕</button>
        </div>

        {/* Progress Steps */}
        <div className="form-steps">
          {steps.map(step => (
            <div 
              key={step.number}
              className={`step ${currentStep === step.number ? 'active' : ''} ${currentStep > step.number ? 'completed' : ''}`}
              onClick={() => setCurrentStep(step.number)}
            >
              <div className="step-icon">{step.icon}</div>
              <div className="step-label">{step.title}</div>
            </div>
          ))}
        </div>

        <div className="camp-form-body">
          {/* Step 1: Basic Info */}
          {currentStep === 1 && (
            <div className="form-step">
              <h3>Basic Information</h3>
              
              <div className="form-group">
                <label>Camp Name *</label>
                <input
                  type="text"
                  name="name"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="e.g., Winter Robotics Camp 2025"
                  required
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Camp Type *</label>
                  <select name="camp_type" value={formData.camp_type} onChange={handleChange}>
                    <option value="winter_camp">Winter Camp</option>
                    <option value="summer_camp">Summer Camp</option>
                    <option value="spring_break_camp">Spring Break Camp</option>
                    <option value="pro_d_day_camp">Pro-D Day Camp</option>
                    <option value="holiday_camp">Holiday Camp</option>
                    <option value="specialty_camp">Specialty Camp</option>
                  </select>
                </div>

                <div className="form-group">
                  <label>Applicable Tiers *</label>
                  <div className="checkbox-group" style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
                    {['foundations', 'innovators', 'pioneers'].map(tier => (
                      <label key={tier} style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                        <input
                          type="checkbox"
                          checked={formData.applicable_tiers.includes(tier)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setFormData(prev => ({
                                ...prev,
                                applicable_tiers: [...prev.applicable_tiers, tier]
                              }));
                            } else {
                              setFormData(prev => ({
                                ...prev,
                                applicable_tiers: prev.applicable_tiers.filter(t => t !== tier)
                              }));
                            }
                          }}
                          style={{ marginRight: '8px', width: '18px', height: '18px' }}
                        />
                        <span style={{ textTransform: 'capitalize' }}>{tier}</span>
                      </label>
                    ))}
                  </div>
                  <small>Select which student tiers can enroll in this camp</small>
                </div>
              </div>

              <div className="form-group">
                <label>Theme</label>
                <input
                  type="text"
                  name="theme"
                  value={formData.theme}
                  onChange={handleChange}
                  placeholder="e.g., LEGO Mindstorms & Coding"
                />
              </div>

              <div className="form-group">
                <label>Description</label>
                <textarea
                  name="description"
                  value={formData.description}
                  onChange={handleChange}
                  placeholder="Describe the camp, what students will learn, activities included, etc."
                  rows={4}
                />
              </div>

              <div className="form-group">
                <label>Note (Optional)</label>
                <textarea
                  name="note"
                  value={formData.note}
                  onChange={handleChange}
                  rows={3}
                  placeholder="Optional note displayed on camp card (e.g., 'Students will be grouped into 1 or more groups based on age, experience etc. (eg: Foundations (age 6 to 10), Innovator (age 9 to 15))')"
                />
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '4px' }}>
                  This note will be displayed on the camp card for parents to see.
                </small>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Start Date *</label>
                  <input
                    type="date"
                    name="start_date"
                    value={formData.start_date}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label>End Date *</label>
                  <input
                    type="date"
                    name="end_date"
                    value={formData.end_date}
                    onChange={handleChange}
                    required
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Age Range (Min)</label>
                  <input
                    type="number"
                    name="age_min"
                    value={formData.age_min}
                    onChange={handleChange}
                    min="4"
                    max="18"
                  />
                </div>

                <div className="form-group">
                  <label>Age Range (Max)</label>
                  <input
                    type="number"
                    name="age_max"
                    value={formData.age_max}
                    onChange={handleChange}
                    min="4"
                    max="18"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Schedule */}
          {currentStep === 2 && (
            <div className="form-step">
              <h3>Daily Schedule & Timing</h3>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Camp Start Time *</label>
                  <input
                    type="time"
                    name="camp_start_time"
                    value={formData.camp_start_time}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Camp End Time *</label>
                  <input
                    type="time"
                    name="camp_end_time"
                    value={formData.camp_end_time}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <h4>Break Times</h4>
              
              <div className="form-row">
                <div className="form-group">
                  <label>Snack Time Start</label>
                  <input
                    type="time"
                    name="snack_time_start"
                    value={formData.snack_time_start}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Snack Time End</label>
                  <input
                    type="time"
                    name="snack_time_end"
                    value={formData.snack_time_end}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Lunch Break Start</label>
                  <input
                    type="time"
                    name="lunch_break_start"
                    value={formData.lunch_break_start}
                    onChange={handleChange}
                  />
                </div>

                <div className="form-group">
                  <label>Lunch Break End</label>
                  <input
                    type="time"
                    name="lunch_break_end"
                    value={formData.lunch_break_end}
                    onChange={handleChange}
                  />
                </div>
              </div>

              <h4>Extended Hours (Optional)</h4>
              
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="early_dropoff_enabled"
                    checked={formData.early_dropoff_enabled}
                    onChange={handleChange}
                  />
                  Enable Early Drop-off
                </label>
              </div>

              {formData.early_dropoff_enabled && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Early Drop-off Start</label>
                    <input
                      type="time"
                      name="early_dropoff_start"
                      value={formData.early_dropoff_start}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Early Drop-off End</label>
                    <input
                      type="time"
                      name="early_dropoff_end"
                      value={formData.early_dropoff_end}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Fee (per day)</label>
                    <input
                      type="number"
                      name="early_dropoff_fee"
                      value={formData.early_dropoff_fee}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              )}

              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="late_pickup_enabled"
                    checked={formData.late_pickup_enabled}
                    onChange={handleChange}
                  />
                  Enable Late Pick-up
                </label>
              </div>

              {formData.late_pickup_enabled && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Late Pick-up Start</label>
                    <input
                      type="time"
                      name="late_pickup_start"
                      value={formData.late_pickup_start}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Late Pick-up End</label>
                    <input
                      type="time"
                      name="late_pickup_end"
                      value={formData.late_pickup_end}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="form-group">
                    <label>Fee (per day)</label>
                    <input
                      type="number"
                      name="late_pickup_fee"
                      value={formData.late_pickup_fee}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                    />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Step 3: Pricing */}
          {currentStep === 3 && (
            <div className="form-step">
              <h3>Pricing & Capacity</h3>
              
              {/* Tier Pricing Model */}
              <div className="form-group" style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>Tier Pricing Model *</label>
                <div style={{ display: 'flex', gap: '20px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="tier_pricing_model"
                      value="same_price"
                      checked={formData.tier_pricing_model === 'same_price'}
                      onChange={(e) => setFormData(prev => ({ ...prev, tier_pricing_model: e.target.value }))}
                      style={{ marginRight: '8px' }}
                    />
                    <span>Same Price for All Tiers</span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="tier_pricing_model"
                      value="different_price"
                      checked={formData.tier_pricing_model === 'different_price'}
                      onChange={(e) => setFormData(prev => ({ ...prev, tier_pricing_model: e.target.value }))}
                      style={{ marginRight: '8px' }}
                    />
                    <span>Different Price per Tier</span>
                  </label>
                </div>
                <small>Choose whether pricing is the same for all tiers or different per tier</small>
              </div>
              
              <div className="form-group">
                <label>Pricing Model *</label>
                <select name="pricing_model" value={formData.pricing_model} onChange={handleChange} required>
                  <option value="full_camp">Full Camp Only (Single price for entire camp)</option>
                  <option value="weekly">Weekly Only (Price per week)</option>
                  <option value="drop_in">Drop-in Only (Price per day)</option>
                  <option value="weekly_and_drop_in">Weekly & Drop-in (Both options available)</option>
                </select>
                <small>Choose how parents can register for this camp</small>
              </div>

              {/* Full Camp Price */}
              {(formData.pricing_model === 'full_camp') && (
                <div className="form-group">
                  <label>Full Camp Price *</label>
                  <input
                    type="number"
                    name="base_price"
                    value={formData.base_price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    placeholder="299.00"
                  />
                  <small>Total price for the entire camp</small>
                </div>
              )}

              {/* Weekly Price */}
              {(formData.pricing_model === 'weekly' || formData.pricing_model === 'weekly_and_drop_in') && (
                <div className="form-row">
                  <div className="form-group">
                    <label>Weekly Price *</label>
                    <input
                      type="number"
                      name="weekly_price"
                      value={formData.weekly_price}
                      onChange={handleChange}
                      step="0.01"
                      min="0"
                      required
                      placeholder="200.00"
                    />
                    <small>Price per week</small>
                  </div>

                  <div className="form-group">
                    <label>Total Weeks *</label>
                    <input
                      type="number"
                      name="total_weeks"
                      value={formData.total_weeks}
                      onChange={handleChange}
                      min="1"
                      required
                    />
                    <small>How many weeks in this camp?</small>
                  </div>
                </div>
              )}

              {/* Drop-in Price */}
              {(formData.pricing_model === 'drop_in' || formData.pricing_model === 'weekly_and_drop_in') && (
                <div className="form-group">
                  <label>Drop-in Price (Per Day) *</label>
                  <input
                    type="number"
                    name="drop_in_price"
                    value={formData.drop_in_price}
                    onChange={handleChange}
                    step="0.01"
                    min="0"
                    required
                    placeholder="60.00"
                  />
                  <small>Price per single day</small>
                </div>
              )}

              {/* Tier-Specific Pricing (when different_price is selected) */}
              {formData.tier_pricing_model === 'different_price' && (
                <div style={{ marginTop: '30px', padding: '20px', backgroundColor: '#e7f3ff', borderRadius: '8px', border: '2px solid #007bff' }}>
                  <h4 style={{ marginTop: 0, marginBottom: '15px', color: '#007bff' }}>💰 Tier-Specific Pricing</h4>
                  {formData.applicable_tiers.map(tier => (
                    <div key={tier} style={{ marginBottom: '20px', padding: '15px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #ddd' }}>
                      <h5 style={{ marginTop: 0, marginBottom: '10px', textTransform: 'capitalize' }}>{tier} Tier</h5>
                      {formData.pricing_model === 'full_camp' && (
                        <div className="form-group">
                          <label>Full Camp Price *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.tier_pricing[tier]?.base_price || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                tier_pricing: {
                                  ...prev.tier_pricing,
                                  [tier]: { ...prev.tier_pricing[tier], base_price: e.target.value }
                                }
                              }));
                            }}
                            placeholder="299.00"
                            required
                          />
                        </div>
                      )}
                      {(formData.pricing_model === 'weekly' || formData.pricing_model === 'weekly_and_drop_in') && (
                        <div className="form-group">
                          <label>Weekly Price *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.tier_pricing[tier]?.weekly_price || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                tier_pricing: {
                                  ...prev.tier_pricing,
                                  [tier]: { ...prev.tier_pricing[tier], weekly_price: e.target.value }
                                }
                              }));
                            }}
                            placeholder="200.00"
                            required
                          />
                        </div>
                      )}
                      {(formData.pricing_model === 'drop_in' || formData.pricing_model === 'weekly_and_drop_in') && (
                        <div className="form-group">
                          <label>Drop-in Price (Per Day) *</label>
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            value={formData.tier_pricing[tier]?.drop_in_price || ''}
                            onChange={(e) => {
                              setFormData(prev => ({
                                ...prev,
                                tier_pricing: {
                                  ...prev.tier_pricing,
                                  [tier]: { ...prev.tier_pricing[tier], drop_in_price: e.target.value }
                                }
                              }));
                            }}
                            placeholder="60.00"
                            required
                          />
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Capacity & Min Enrollment */}
              <div className="form-row">
                <div className="form-group">
                  <label>Capacity *</label>
                  <input
                    type="number"
                    name="capacity"
                    value={formData.capacity}
                    onChange={handleChange}
                    min="1"
                    required
                  />
                  <small>Maximum number of students</small>
                </div>

                <div className="form-group">
                  <label>Minimum Enrollment</label>
                  <input
                    type="number"
                    name="min_enrollment"
                    value={formData.min_enrollment}
                    onChange={handleChange}
                    min="1"
                  />
                  <small>Minimum students needed to run camp</small>
                </div>
              </div>

              {/* Stripe Mode Toggle */}
              <div className="form-group" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px', border: '2px solid #007bff' }}>
                <label style={{ display: 'flex', alignItems: 'center', fontSize: '16px', fontWeight: 'bold', color: '#007bff' }}>
                  <span style={{ marginRight: '10px' }}>💳</span>
                  Stripe Payment Mode *
                </label>
                <div style={{ marginTop: '10px' }}>
                  <label style={{ display: 'flex', alignItems: 'center', marginBottom: '8px', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stripe_mode"
                      value="test"
                      checked={formData.stripe_mode === 'test'}
                      onChange={handleChange}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      <strong>Test Mode</strong> - Use Stripe test keys (for testing, no real charges)
                    </span>
                  </label>
                  <label style={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}>
                    <input
                      type="radio"
                      name="stripe_mode"
                      value="live"
                      checked={formData.stripe_mode === 'live'}
                      onChange={handleChange}
                      style={{ marginRight: '8px', width: '18px', height: '18px' }}
                    />
                    <span style={{ fontSize: '14px' }}>
                      <strong style={{ color: '#dc3545' }}>LIVE Mode</strong> - Use Stripe live keys (REAL CHARGES will be made)
                    </span>
                  </label>
                </div>
                <small style={{ display: 'block', marginTop: '10px', color: '#666', fontSize: '12px' }}>
                  {formData.stripe_mode === 'live' 
                    ? '⚠️ WARNING: Live mode will process REAL payments. Only use for production camps.' 
                    : '✅ Test mode is safe for testing. No real charges will be made.'}
                </small>
              </div>

              <div className="pricing-info">
                <h4>💡 Pricing Example</h4>
                {formData.pricing_model === 'weekly_and_drop_in' && (
                  <ul>
                    <li><strong>Weekly:</strong> ${formData.weekly_price || '200'} × {formData.total_weeks || '2'} weeks = ${(parseFloat(formData.weekly_price || '200') * parseInt(formData.total_weeks || '2')).toFixed(2)}</li>
                    <li><strong>Drop-in:</strong> ${formData.drop_in_price || '60'} per day</li>
                  </ul>
                )}
                <h4>💡 Member Discounts (Auto-calculated at checkout)</h4>
                <ul>
                  <li>Foundations: 10% off</li>
                  <li>Innovators: 15% off</li>
                  <li>Pioneers: 20% off</li>
                </ul>
              </div>
            </div>
          )}

          {/* Step 4: Details */}
          {currentStep === 4 && (
            <div className="form-step">
              <h3>Camp Details</h3>
              
              <h4>Location</h4>
              <div className="form-group">
                <label>Location Name</label>
                <input
                  type="text"
                  name="location_name"
                  value={formData.location_name}
                  onChange={handleChange}
                  placeholder="e.g., OARA Main Lab"
                />
              </div>

              <div className="form-group">
                <label>Location Address</label>
                <input
                  type="text"
                  name="location_address"
                  value={formData.location_address}
                  onChange={handleChange}
                  placeholder="Full address"
                />
              </div>

              <div className="form-group">
                <label>Room Number</label>
                <input
                  type="text"
                  name="room_number"
                  value={formData.room_number}
                  onChange={handleChange}
                />
              </div>

              <h4>Materials</h4>
              <div className="form-group">
                <label>Materials Provided (comma-separated)</label>
                <textarea
                  name="materials_provided"
                  value={formData.materials_provided}
                  onChange={handleChange}
                  placeholder="e.g., LEGO Mindstorms Kit, Laptop, Workbooks"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Materials Students Should Bring (comma-separated)</label>
                <textarea
                  name="materials_students_bring"
                  value={formData.materials_students_bring}
                  onChange={handleChange}
                  placeholder="e.g., Water bottle, Nut-free snack, Closed-toe shoes"
                  rows={2}
                />
              </div>

              <h4>Food & Dietary</h4>
              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="lunch_provided"
                    checked={formData.lunch_provided}
                    onChange={handleChange}
                  />
                  Lunch Provided
                </label>
              </div>

              <div className="checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    name="snacks_provided"
                    checked={formData.snacks_provided}
                    onChange={handleChange}
                  />
                  Snacks Provided
                </label>
              </div>

              <div className="form-group">
                <label>Dietary Notes</label>
                <textarea
                  name="dietary_restrictions_notes"
                  value={formData.dietary_restrictions_notes}
                  onChange={handleChange}
                  placeholder="Any dietary restrictions or allergy information parents should know"
                  rows={2}
                />
              </div>

              <h4>Requirements</h4>
              <div className="form-group">
                <label>Prerequisites</label>
                <textarea
                  name="prerequisites"
                  value={formData.prerequisites}
                  onChange={handleChange}
                  placeholder="e.g., Basic computer skills, completed Foundations program"
                  rows={2}
                />
              </div>

              <div className="form-group">
                <label>Staff Ratio</label>
                <input
                  type="text"
                  name="staff_ratio"
                  value={formData.staff_ratio}
                  onChange={handleChange}
                  placeholder="e.g., 1:8"
                />
              </div>
            </div>
          )}

          {/* Step 5: Policies */}
          {currentStep === 5 && (
            <div className="form-step">
              <h3>Policies & Terms</h3>
              
              <div className="form-group">
                <label>Cancellation Policy</label>
                <textarea
                  name="cancellation_policy"
                  value={formData.cancellation_policy}
                  onChange={handleChange}
                  placeholder="e.g., Full refund if cancelled 7+ days before camp. 50% refund 3-6 days before. No refund within 2 days."
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label>Refund Policy</label>
                <textarea
                  name="refund_policy"
                  value={formData.refund_policy}
                  onChange={handleChange}
                  placeholder="e.g., Refunds processed within 5-7 business days to original payment method"
                  rows={3}
                />
              </div>

              <div className="form-group">
                <label style={{ display: 'flex', alignItems: 'center', gap: '10px', cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    name="discount_codes_enabled"
                    checked={formData.discount_codes_enabled}
                    onChange={(e) => setFormData(prev => ({ ...prev, discount_codes_enabled: e.target.checked }))}
                  />
                  <span>Enable Discount Codes for this Camp</span>
                </label>
                <small style={{ color: '#666', fontSize: '12px', display: 'block', marginTop: '5px' }}>
                  When enabled, parents will see a discount code input field during camp enrollment
                </small>
              </div>

              <div className="summary-box">
                <h4>📋 Camp Summary</h4>
                <div className="summary-grid">
                  <div><strong>Name:</strong> {formData.name || 'Not set'}</div>
                  <div><strong>Type:</strong> {formData.camp_type}</div>
                  <div><strong>Dates:</strong> {formData.start_date} to {formData.end_date}</div>
                  <div><strong>Price:</strong> ${formData.base_price}</div>
                  <div><strong>Capacity:</strong> {formData.capacity} students</div>
                  <div><strong>Schedule:</strong> {formData.camp_start_time} - {formData.camp_end_time}</div>
                </div>
              </div>
            </div>
          )}

          {/* Step 6: Day Configuration */}
          {currentStep === 6 && (
            <div className="form-step">
              <h3>📅 Configure Camp Days</h3>
              <p className="step-description">
                Select which days camp runs, assign weeks, and mark holidays.
                {campDays.length > 0 && ` ${campDays.length} days generated from ${formData.start_date} to ${formData.end_date}`}
              </p>

              {campDays.length === 0 ? (
                <div className="info-box">
                  <p>⚠️ Please go back and set start/end dates first.</p>
                </div>
              ) : (
                <div className="days-grid" style={{ maxHeight: '500px', overflowY: 'auto' }}>
                  {campDays.map((day, index) => (
                    <div key={index} className="day-config-card" style={{
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      padding: '15px',
                      marginBottom: '10px',
                      backgroundColor: day.isAvailable ? '#f8f9fa' : '#ffe6e6'
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '10px' }}>
                            <input
                              type="checkbox"
                              checked={day.isAvailable}
                              onChange={(e) => updateDay(index, 'isAvailable', e.target.checked)}
                              style={{ width: '20px', height: '20px' }}
                            />
                            <strong style={{ fontSize: '16px' }}>
                              Day {day.dayNumber}: {day.dayOfWeek}, {new Date(day.date + 'T12:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                            </strong>
                          </div>

                          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '10px', marginLeft: '30px' }}>
                            <div>
                              <label style={{ fontSize: '12px', color: '#666' }}>Week Number</label>
                              <select
                                value={day.weekNumber}
                                onChange={(e) => updateDay(index, 'weekNumber', parseInt(e.target.value))}
                                style={{ width: '100%', padding: '5px', marginTop: '3px' }}
                                disabled={!day.isAvailable}
                              >
                                {Array.from({ length: parseInt(formData.total_weeks) }, (_, i) => i + 1).map(week => (
                                  <option key={week} value={week}>Week {week}</option>
                                ))}
                              </select>
                            </div>

                            {!day.isAvailable && (
                              <div>
                                <label style={{ fontSize: '12px', color: '#666' }}>Reason (Holiday)</label>
                                <input
                                  type="text"
                                  value={day.unavailableReason}
                                  onChange={(e) => updateDay(index, 'unavailableReason', e.target.value)}
                                  placeholder="e.g., Stat Holiday - Christmas"
                                  style={{ width: '100%', padding: '5px', marginTop: '3px' }}
                                />
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              <div className="days-summary" style={{ marginTop: '20px', padding: '15px', backgroundColor: '#e7f3ff', borderRadius: '8px' }}>
                <h4>📊 Summary</h4>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '15px' }}>
                  <div>
                    <strong>Total Days:</strong> {campDays.length}
                  </div>
                  <div>
                    <strong>Available Days:</strong> {campDays.filter(d => d.isAvailable).length}
                  </div>
                  <div>
                    <strong>Holidays/Off:</strong> {campDays.filter(d => !d.isAvailable).length}
                  </div>
                  {Array.from({ length: parseInt(formData.total_weeks) }, (_, i) => i + 1).map(week => (
                    <div key={week}>
                      <strong>Week {week} Days:</strong> {campDays.filter(d => d.weekNumber === week && d.isAvailable).length}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="camp-form-footer">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          
          <div className="footer-actions">
            {currentStep > 1 && (
              <button className="btn btn-outline" onClick={prevStep}>
                ← Previous
              </button>
            )}
            
            {currentStep < 6 ? (
              <button className="btn btn-primary" onClick={nextStep}>
                Next →
              </button>
            ) : (
              <button 
                className="btn btn-success" 
                onClick={handleSubmit}
                disabled={loading}
              >
                {loading ? 'Saving...' : (camp ? '💾 Update Camp' : '✅ Create Camp')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CampForm;

